import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useAuth } from '../contexts/AuthContext';
import { reportingService } from '../services/reportingService';
import { tenantService } from '../services/tenantService';
import { subscriptionService } from '../services/subscriptionService';
import { usersService } from '../services/usersService';
import { rolesService } from '../services/rolesService';
import { permissionsService } from '../services/permissionsService';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

interface KPIData {
  totalUsers: number;
  activeUsers: number;
  totalTenants: number;
  activeTenants: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  totalRoles: number;
  totalPermissions: number;
  userGrowthRate: number;
  tenantGrowthRate: number;
  subscriptionGrowthRate: number;
  userActivityTrend: Array<{
    date: string;
    activeUsers: number;
    newUsers: number;
  }>;
  tenantUsageTrend: Array<{
    date: string;
    activeTenants: number;
    newTenants: number;
  }>;
  subscriptionTrend: Array<{
    date: string;
    activeSubscriptions: number;
    newSubscriptions: number;
  }>;
  roleDistribution: Array<{
    roleName: string;
    userCount: number;
  }>;
  permissionDistribution: Array<{
    permissionName: string;
    roleCount: number;
  }>;
}

const KPIs: React.FC = () => {
  const { tokens, login } = useAuth();
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  
  const [kpiData, setKpiData] = useState<KPIData | null>(null);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [aggregationPeriod, setAggregationPeriod] = useState<'day' | 'week' | 'month' | 'year'>('day');

  // Get valid aggregation periods based on time range
  const getValidAggregationPeriods = (range: '7d' | '30d' | '90d' | '1y'): Array<'day' | 'week' | 'month' | 'year'> => {
    switch (range) {
      case '7d':
        return ['day', 'week']; // 7 days: can view by day or week
      case '30d':
        return ['day', 'week', 'month']; // 30 days: can view by day, week, or month
      case '90d':
        return ['day', 'week', 'month']; // 90 days: can view by day, week, or month
      case '1y':
        return ['day', 'week', 'month', 'year']; // 1 year: can view by any period
      default:
        return ['day', 'week', 'month', 'year'];
    }
  };

  // Auto-adjust aggregation period if it becomes invalid when time range changes
  useEffect(() => {
    const validPeriods = getValidAggregationPeriods(timeRange);
    if (!validPeriods.includes(aggregationPeriod)) {
      // Auto-select the most granular valid period (first in the array)
      setAggregationPeriod(validPeriods[0]);
    }
  }, [timeRange, aggregationPeriod]);

  // Initialize services and load data
  useEffect(() => {
    if (tokens && login) {
      reportingService.initialize(tokens, login);
      tenantService.initialize(tokens, login);
      subscriptionService.initialize(tokens, login);
      usersService.initialize(tokens, login);
      rolesService.initialize(tokens, login);
      permissionsService.initialize(tokens, login);
      loadKPIData();
    }
  }, [tokens, login, timeRange]);

  const loadKPIData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Calculate date range based on timeRange selection
      const getDaysForTimeRange = (range: '7d' | '30d' | '90d' | '1y') => {
        switch (range) {
          case '7d': return 7;
          case '30d': return 30;
          case '90d': return 90;
          case '1y': return 365;
          default: return 30;
        }
      };

      const days = getDaysForTimeRange(timeRange);
      const endDate = new Date();
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - days);
      
      // Format dates as ISO strings for API
      const startDateISO = startDate.toISOString();
      
      // Helper function to fetch all pages of data
      const fetchAllPages = async (
        serviceMethod: (params: any) => Promise<any>,
        entityName: string,
        additionalParams: any = {}
      ) => {
        const allItems: any[] = [];
        let page = 1;
        const pageSize = 100;
        let hasMore = true;

        while (hasMore) {
          try {
            const response = await serviceMethod({ page, pageSize, ...additionalParams });
            const items = response?.data?.items || response?.data || [];
            
            if (Array.isArray(items) && items.length > 0) {
              allItems.push(...items);
              
              // Check if there are more pages
              const totalCount = response?.data?.totalCount || response?.totalCount || 0;
              const pageCount = response?.data?.pageCount || response?.pageCount || 1;
              
              if (page >= pageCount || allItems.length >= totalCount) {
                hasMore = false;
              } else {
                page++;
              }
            } else {
              hasMore = false;
            }
          } catch (error) {
            console.error(`Error fetching ${entityName} page ${page}:`, error);
            hasMore = false;
          }
        }

        return allItems;
      };

      // Fetch all data with pagination support and date filters
      // For users, filter by startDate (subscription start) within the time range
      const [users, tenants, subscriptions, roles, permissions] = await Promise.all([
        fetchAllPages((params) => usersService.getUsers(params), 'users', {
          startDate: startDateISO,
        }),
        fetchAllPages((params) => tenantService.getTenants(params), 'tenants'),
        fetchAllPages((params) => subscriptionService.getSubscriptions(params), 'subscriptions'),
        fetchAllPages((params) => rolesService.getRoles(params), 'roles'),
        fetchAllPages((params) => permissionsService.getPermissions(params), 'permissions'),
      ]);

      // Calculate basic metrics
      const totalUsers = users.length;
      const activeUsers = users.filter((user: any) => user.isActive !== false).length;
      const totalTenants = tenants.length;
      const activeTenants = tenants.filter((tenant: any) => tenant.isActive !== false).length;
      const totalSubscriptions = subscriptions.length;
      const activeSubscriptions = subscriptions.filter((sub: any) => sub.status === 0).length;
      const totalRoles = roles.length;
      const totalPermissions = permissions.length;

      // Calculate growth rates based on date data
      const calculateGrowthRate = (items: any[], dateField: string, days: number = 30) => {
        if (items.length === 0) return 0;
        
        const now = new Date();
        const periodStart = new Date(now);
        periodStart.setDate(periodStart.getDate() - days);
        const previousPeriodStart = new Date(periodStart);
        previousPeriodStart.setDate(previousPeriodStart.getDate() - days);

        const currentPeriod = items.filter((item: any) => {
          // Use the provided dateField directly, don't fallback to createdAt
          const date = new Date(item[dateField] || now);
          return date >= periodStart;
        }).length;

        const previousPeriod = items.filter((item: any) => {
          const date = new Date(item[dateField] || now);
          return date >= previousPeriodStart && date < periodStart;
        }).length;

        if (previousPeriod === 0) return currentPeriod > 0 ? 100 : 0;
        
        return ((currentPeriod - previousPeriod) / previousPeriod) * 100;
      };

      // Use startDate for users (subscription start date) and createdAt for tenants
      const userGrowthRate = calculateGrowthRate(users, 'startDate', 30);
      const tenantGrowthRate = calculateGrowthRate(tenants, 'createdAt', 30);
      const subscriptionGrowthRate = calculateGrowthRate(subscriptions, 'startDate', 30);

      const generateTrendData = (
        items: any[],
        dateField: string,
        activeFilter: (item: any) => boolean,
        days: number,
        trendType: 'user' | 'tenant' | 'subscription'
      ) => {
        const data: Array<{
          date: string;
          activeUsers?: number;
          newUsers?: number;
          activeTenants?: number;
          newTenants?: number;
          activeSubscriptions?: number;
          newSubscriptions?: number;
        }> = [];
        const today = new Date();
        
        // Group items by date
        const dateMap = new Map<string, { active: number; new: number }>();
        
        for (let i = days; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];
          dateMap.set(dateStr, { active: 0, new: 0 });
        }

        // Helper to normalize date to start of day for consistent comparison
        const normalizeToDate = (date: Date): Date => {
          const normalized = new Date(date);
          normalized.setHours(0, 0, 0, 0);
          return normalized;
        };

        const todayNormalized = normalizeToDate(today);

        items.forEach((item: any) => {
          try {
            // For users, use startDate (subscription start) instead of createdAt
            const itemDate = new Date(item[dateField] || today);
            const normalizedItemDate = normalizeToDate(itemDate);
            const dateStr = normalizedItemDate.toISOString().split('T')[0];
            const todayStr = todayNormalized.toISOString().split('T')[0];
            
            if (dateStr <= todayStr && dateMap.has(dateStr)) {
              const entry = dateMap.get(dateStr)!;
              if (activeFilter(item)) {
                entry.active++;
              }
              entry.new++;
            }
          } catch (e) {
            // Skip items with invalid dates
            console.warn('Invalid date for item:', item);
          }
        });

        // Calculate cumulative values for each date
        // Count all items that are currently active and were created on or before each date
        const sortedDates = Array.from(dateMap.keys()).sort();
        sortedDates.forEach((dateStr) => {
          const currentDateNormalized = normalizeToDate(new Date(dateStr));
          const activeItemsUpToDate = items.filter((item: any) => {
            try {
              // For users, use startDate (subscription start) instead of createdAt
              const itemDate = normalizeToDate(new Date(item[dateField] || today));
              // Item must be created on or before this date and pass the active filter
              return itemDate <= currentDateNormalized && activeFilter(item);
            } catch (e) {
              return false;
            }
          }).length;
          
          const entry = dateMap.get(dateStr)!;
          
          const dataPoint: any = { date: dateStr };
          
          if (trendType === 'user') {
            dataPoint.activeUsers = activeItemsUpToDate;
            dataPoint.newUsers = entry.new;
          } else if (trendType === 'tenant') {
            dataPoint.activeTenants = activeItemsUpToDate;
            dataPoint.newTenants = entry.new;
          } else if (trendType === 'subscription') {
            dataPoint.activeSubscriptions = activeItemsUpToDate;
            dataPoint.newSubscriptions = entry.new;
          }
          
          data.push(dataPoint);
        });

        return data;
      };

      // Use startDate for users (subscription start date)
      const userActivityTrend = generateTrendData(
        users,
        'startDate',
        (user: any) => user.isActive !== false,
        days,
        'user'
      ).map(item => ({
        date: item.date,
        activeUsers: item.activeUsers || 0,
        newUsers: item.newUsers || 0,
      }));

      const tenantUsageTrend = generateTrendData(
        tenants,
        'createdAt',
        (tenant: any) => tenant.isActive !== false,
        days,
        'tenant'
      ).map(item => ({
        date: item.date,
        activeTenants: item.activeTenants || 0,
        newTenants: item.newTenants || 0,
      }));

      const subscriptionTrend = generateTrendData(
        subscriptions,
        'startDate',
        (sub: any) => sub.status === 0,
        days,
        'subscription'
      ).map(item => ({
        date: item.date,
        activeSubscriptions: item.activeSubscriptions || 0,
        newSubscriptions: item.newSubscriptions || 0,
      }));

      // Calculate role distribution using real data
      const roleDistribution = roles
        .filter((role: any) => role.name && role.totalUserCount !== undefined)
        .map((role: any) => ({
          roleName: role.name,
          userCount: role.totalUserCount || 0,
        }))
        .sort((a, b) => b.userCount - a.userCount);

      // Calculate permission distribution using real data
      const permissionDistribution = permissions
        .filter((permission: any) => permission.name)
        .map((permission: any) => ({
          permissionName: permission.name,
          roleCount: permission.roles ? (Array.isArray(permission.roles) ? permission.roles.length : 0) : 0,
        }))
        .sort((a, b) => b.roleCount - a.roleCount);

      const kpiData: KPIData = {
        totalUsers,
        activeUsers,
        totalTenants,
        activeTenants,
        totalSubscriptions,
        activeSubscriptions,
        totalRoles,
        totalPermissions,
        userGrowthRate: Math.round(userGrowthRate * 10) / 10,
        tenantGrowthRate: Math.round(tenantGrowthRate * 10) / 10,
        subscriptionGrowthRate: Math.round(subscriptionGrowthRate * 10) / 10,
        userActivityTrend,
        tenantUsageTrend,
        subscriptionTrend,
        roleDistribution,
        permissionDistribution,
      };

      setKpiData(kpiData);
    } catch (err) {
      console.error('Error loading KPI data:', err);
      setError('Failed to load KPI data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadKPIData();
  };


  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Helper function to aggregate data by period
  const aggregateDataByPeriod = (
    data: Array<{ date: string; activeUsers: number; newUsers: number }>,
    period: 'day' | 'week' | 'month' | 'year'
  ) => {
    if (period === 'day') {
      return data.map(item => ({
        period: item.date,
        activeUsers: item.activeUsers,
        newUsers: item.newUsers,
      }));
    }

    const aggregated = new Map<string, { activeUsers: number; newUsers: number; maxActiveUsers: number }>();

    data.forEach(item => {
      const date = new Date(item.date);
      let periodKey = '';

      switch (period) {
        case 'week': {
          // Get the start of the week (Monday)
          const weekStart = new Date(date);
          const day = weekStart.getDay();
          const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
          weekStart.setDate(diff);
          weekStart.setHours(0, 0, 0, 0);
          // Use ISO week format: YYYY-Www
          const weekNum = getWeekNumber(weekStart);
          const year = weekStart.getFullYear();
          periodKey = `${year}-W${weekNum}`;
          break;
        }
        case 'month': {
          periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          break;
        }
        case 'year': {
          periodKey = String(date.getFullYear());
          break;
        }
      }

      if (!aggregated.has(periodKey)) {
        aggregated.set(periodKey, {
          activeUsers: 0,
          newUsers: 0,
          maxActiveUsers: 0,
        });
      }

      const entry = aggregated.get(periodKey)!;
      entry.newUsers += item.newUsers;
      // For active users, take the maximum value in the period (since it's cumulative)
      entry.maxActiveUsers = Math.max(entry.maxActiveUsers, item.activeUsers);
    });

    // Convert map to array and sort by period
    const result = Array.from(aggregated.entries())
      .map(([period, values]) => ({
        period,
        activeUsers: values.maxActiveUsers,
        newUsers: values.newUsers,
      }))
      .sort((a, b) => {
        // Sort by period value
        if (period === 'year') {
          return parseInt(a.period) - parseInt(b.period);
        } else if (period === 'month') {
          return a.period.localeCompare(b.period);
        } else if (period === 'week') {
          return a.period.localeCompare(b.period);
        }
        return 0;
      });

    return result;
  };

  // Helper function to get week number
  const getWeekNumber = (date: Date): string => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return String(Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)).padStart(2, '0');
  };

  // Format period labels for display
  const formatPeriodLabel = (period: string, aggregationType: 'day' | 'week' | 'month' | 'year'): string => {
    switch (aggregationType) {
      case 'day': {
        const date = new Date(period);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      case 'week': {
        // Format: "YYYY-WXX"
        const [year, week] = period.split('-W');
        return `${year} Week ${parseInt(week)}`;
      }
      case 'month': {
        // Format: "YYYY-MM"
        const [year, month] = period.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      }
      case 'year': {
        return period;
      }
      default:
        return period;
    }
  };

  // Chart configurations
  const determineUserActivityChartData = () => {
    if (!kpiData) return null;

    const aggregatedData = aggregateDataByPeriod(kpiData.userActivityTrend, aggregationPeriod);

    return {
      labels: aggregatedData.map(item => formatPeriodLabel(item.period, aggregationPeriod)),
      datasets: [
        {
          label: 'Active Users',
          data: aggregatedData.map(item => item.activeUsers),
          borderColor: '#2196f3',
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
          tension: 0.1,
        },
        {
          label: 'New Users',
          data: aggregatedData.map(item => item.newUsers),
          borderColor: '#4caf50',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          tension: 0.1,
        },
      ],
    };
  };

  const userActivityChartData = determineUserActivityChartData();

  const roleDistributionChartData = kpiData ? {
    labels: kpiData.roleDistribution.map(item => item.roleName),
    datasets: [{
      data: kpiData.roleDistribution.map(item => item.userCount),
      backgroundColor: [
        '#2196f3',
        '#4caf50',
        '#ff9800',
        '#f44336',
        '#9c27b0',
        '#00bcd4',
        '#795548',
        '#607d8b',
      ],
    }],
  } : null;

  const systemOverviewChartData = kpiData ? {
    labels: ['Users', 'Tenants', 'Subscriptions', 'Roles', 'Permissions'],
    datasets: [{
      label: 'Count',
      data: [
        kpiData.totalUsers,
        kpiData.totalTenants,
        kpiData.totalSubscriptions,
        kpiData.totalRoles,
        kpiData.totalPermissions,
      ],
      backgroundColor: [
        '#2196f3',
        '#4caf50',
        '#ff9800',
        '#f44336',
        '#9c27b0',
      ],
    }],
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          KPI Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d' | '1y')}
              label="Time Range"
            >
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
              <MenuItem value="1y">Last Year</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>View By</InputLabel>
            <Select
              value={aggregationPeriod}
              onChange={(e) => setAggregationPeriod(e.target.value as 'day' | 'week' | 'month' | 'year')}
              label="View By"
            >
              {(() => {
                const validPeriods = getValidAggregationPeriods(timeRange);
                return (
                  <>
                    {validPeriods.includes('day') && (
                      <MenuItem value="day">Day</MenuItem>
                    )}
                    {validPeriods.includes('week') && (
                      <MenuItem value="week">Week</MenuItem>
                    )}
                    {validPeriods.includes('month') && (
                      <MenuItem value="month">Month</MenuItem>
                    )}
                    {validPeriods.includes('year') && (
                      <MenuItem value="year">Year</MenuItem>
                    )}
                  </>
                );
              })()}
            </Select>
          </FormControl>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {kpiData && (
        <Grid container spacing={3}>
          {/* Key Metrics Cards */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PeopleIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Users</Typography>
                </Box>
                <Typography variant="h3" sx={{ mb: 1 }}>
                  {kpiData.totalUsers.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {kpiData.activeUsers} active ({((kpiData.activeUsers / kpiData.totalUsers) * 100).toFixed(1)}%)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body2">
                    +{kpiData.userGrowthRate}% this month
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Tenants</Typography>
                </Box>
                <Typography variant="h3" sx={{ mb: 1 }}>
                  {kpiData.totalTenants.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {kpiData.activeTenants} active ({((kpiData.activeTenants / kpiData.totalTenants) * 100).toFixed(1)}%)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body2">
                    +{kpiData.tenantGrowthRate}% this month
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AssignmentIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Subscriptions</Typography>
                </Box>
                <Typography variant="h3" sx={{ mb: 1 }}>
                  {kpiData.totalSubscriptions.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {kpiData.activeSubscriptions} active ({((kpiData.activeSubscriptions / kpiData.totalSubscriptions) * 100).toFixed(1)}%)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body2">
                    +{kpiData.subscriptionGrowthRate}% this month
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SecurityIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Security</Typography>
                </Box>
                <Typography variant="h3" sx={{ mb: 1 }}>
                  {kpiData.totalRoles + kpiData.totalPermissions}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {kpiData.totalRoles} roles, {kpiData.totalPermissions} permissions
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <AssessmentIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body2">
                    Security metrics
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Charts */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  User Activity Trend {aggregationPeriod !== 'day' && `(by ${aggregationPeriod.charAt(0).toUpperCase() + aggregationPeriod.slice(1)})`}
                </Typography>
                {userActivityChartData && (
                  <Line data={userActivityChartData} options={chartOptions} />
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Role Distribution
                </Typography>
                {roleDistributionChartData && (
                  <Doughnut data={roleDistributionChartData} options={chartOptions} />
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Overview
                </Typography>
                {systemOverviewChartData && (
                  <Bar data={systemOverviewChartData} options={chartOptions} />
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Permission Distribution
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {kpiData.permissionDistribution.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{item.permissionName}</Typography>
                      <Chip label={item.roleCount} color="primary" size="small" />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Additional Metrics */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                System Health Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {((kpiData.activeUsers / kpiData.totalUsers) * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      User Activity Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      {((kpiData.activeTenants / kpiData.totalTenants) * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tenant Utilization
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {((kpiData.activeSubscriptions / kpiData.totalSubscriptions) * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Subscription Health
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {kpiData.totalRoles + kpiData.totalPermissions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Security Elements
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KPIs;
